// Shared content for Pixemance — Business-First Digital Presence & Performance Firm.
// All copy is outcome-focused and uses "You/Your" voice.

export const BRAND = {
    name: "Pixemance",
    tagline: "Where Design Meets Performance.",
    positioning: "Business-First Digital Presence & Performance",
    email: "support@pixemance.com",
    location: "Ferozepur City, Punjab",
    socials: {
        instagram: "https://www.instagram.com/pixemance/",
        pinterest: "https://in.pinterest.com/pixemance/",
        facebook: "https://www.facebook.com/pixemance/",
        youtube: "https://www.youtube.com/@Pixemance",
    },
};

export const NAV_LINKS = [
    { label: "Services", href: "/services" },
    { label: "Process", href: "/process" },
    { label: "Work", href: "/work" },
    { label: "About", href: "/about" },
];

export const PILLARS = [
    {
        n: "01",
        title: "Presence",
        body: "Professional positioning that puts your business in the right rooms — long before the first conversation.",
    },
    {
        n: "02",
        title: "Trust",
        body: "Credibility and authority built into every word, image, and detail your prospects encounter.",
    },
    {
        n: "03",
        title: "Performance",
        body: "Lead generation systems engineered to convert attention into qualified enquiries — predictably.",
    },
    {
        n: "04",
        title: "Growth",
        body: "Long-term visibility and infrastructure that compounds while you focus on running the business.",
    },
];

export const SERVICES = [
    {
        n: "01",
        title: "Premium Business Websites",
        kicker: "Positioning",
        body: "A digital headquarters that signals seriousness, builds confidence, and answers the questions buyers won't ask out loud.",
    },
    {
        n: "02",
        title: "High-Conversion Landing Pages",
        kicker: "Performance",
        body: "Single-purpose pages designed around one decision — to turn paid traffic into measurable enquiries.",
    },
    {
        n: "03",
        title: "Google Business Profile Optimization",
        kicker: "Local Visibility",
        body: "Show up first when local intent is highest. Reviews, signals, photography, and structure handled end-to-end.",
    },
    {
        n: "04",
        title: "Social Presence Management",
        kicker: "Reputation",
        body: "A consistent, considered presence on the channels your audience already uses — without the noise.",
    },
    {
        n: "05",
        title: "Lead Generation Systems",
        kicker: "Pipeline",
        body: "End-to-end systems that capture, qualify, and route enquiries to the right people inside your business.",
    },
    {
        n: "06",
        title: "Reputation Systems",
        kicker: "Authority",
        body: "Structured review collection, testimonial capture, and trust signals that compound month over month.",
    },
    {
        n: "07",
        title: "Website Redesigns",
        kicker: "Repositioning",
        body: "A focused upgrade of what you already have — sharper positioning, better conversion, modern execution.",
    },
    {
        n: "08",
        title: "Business Content Systems",
        kicker: "Clarity",
        body: "Editorial-grade content frameworks that explain what you do, who you do it for, and why it matters.",
    },
    {
        n: "09",
        title: "Booking & Appointment Systems",
        kicker: "Conversion",
        body: "Friction-free scheduling that closes the loop between interest and a booked conversation.",
    },
    {
        n: "10",
        title: "Website Care Plans",
        kicker: "Continuity",
        body: "Ongoing performance, security, and content stewardship — so your digital presence never drifts.",
    },
];

export const PROCESS = [
    {
        n: "01",
        title: "Discovery",
        body: "We start by understanding the business — not the website. Audience, offer, economics, and the real bottleneck behind enquiries.",
    },
    {
        n: "02",
        title: "Strategy",
        body: "A clear, written plan: positioning, narrative, structure, and the specific decisions your visitors need to make.",
    },
    {
        n: "03",
        title: "Build",
        body: "Editorial-grade design and engineering. Considered typography, content, and architecture — no template feel.",
    },
    {
        n: "04",
        title: "Launch",
        body: "A controlled, measured rollout with the trust signals, tracking, and search foundation in place from day one.",
    },
    {
        n: "05",
        title: "Optimize",
        body: "Quiet, ongoing refinement based on real behaviour. Your digital presence improves while you run the business.",
    },
];

export const WHY = [
    {
        title: "Business-first thinking",
        body: "Every decision starts with revenue, reputation, and reach — not pixels.",
    },
    {
        title: "Premium execution",
        body: "Editorial design, technical rigor, and considered copy in every detail.",
    },
    {
        title: "Performance-focused systems",
        body: "Built to generate enquiries, not just look good in a portfolio.",
    },
    {
        title: "Long-term partnership",
        body: "We measure success the way you do — quarters and years, not launch day.",
    },
    {
        title: "Clarity over complexity",
        body: "We remove what doesn't earn its place. What remains is the message.",
    },
];

export const TESTIMONIALS = [
    {
        quote: "Within ninety days of relaunch, our enquiry quality changed completely. The conversations we have now are already pre-qualified.",
        name: "Anika Verma",
        role: "Founder",
        company: "Verma Aesthetic Studio",
    },
    {
        quote: "They treated our practice the way a senior consultant would — with restraint and rigor. The new presence reflects the standard of our work.",
        name: "Dr. Marcus Whelan",
        role: "Principal Dentist",
        company: "Whelan Dental Group",
    },
    {
        quote: "We stopped competing on price the moment our new presence went live. Clients now arrive expecting our premium positioning.",
        name: "Rohan Kapoor",
        role: "Managing Partner",
        company: "Northline Realty",
    },
    {
        quote: "What I appreciate most is the quietness of it. No theatrics — just a clearer business that earns more trust at every touchpoint.",
        name: "Sara Lindqvist",
        role: "Director",
        company: "Lindqvist Strategy Institute",
    },
];

export const FAQS = [
    {
        q: "Why not hire a freelancer?",
        a: "Freelancers solve a task. We solve a business problem — positioning, presence, trust, and enquiries — as a single system. The output looks similar; the business outcome rarely does.",
    },
    {
        q: "How long does a project take?",
        a: "A focused engagement typically spans four to eight weeks, depending on scope and how decisive your team is in the discovery phase. We move quickly without rushing the strategy.",
    },
    {
        q: "Do you provide ongoing support?",
        a: "Yes. Once launched, your digital presence is maintained, measured, and quietly improved through our Care Plan — security, content, performance, and conversion refinements.",
    },
    {
        q: "Can you improve an existing website?",
        a: "Often, yes. If the foundations are sound, a focused redesign can recover most of the value of a full rebuild — in less time and at lower cost. We tell you honestly which path is right.",
    },
    {
        q: "What businesses do you work with?",
        a: "Established service businesses — clinics, consultants, institutes, real-estate professionals, and small to medium-sized companies that take their reputation seriously.",
    },
];

export const CASE_STUDIES = [
    {
        sector: "Dental Practice",
        title: "From eight enquiries a month to forty-three — without paid ads.",
        outcome: "+437% enquiries · 90 days",
        body: "A regional dental group with strong clinical work and weak digital presence. We rebuilt positioning, redesigned the site, restructured the Google Business Profile, and installed a reputation system that compounded weekly.",
        metrics: [
            { value: "+437%", label: "Enquiries · 90 days" },
            { value: "4.9★", label: "Avg. review score" },
            { value: "1st", label: "Local map pack" },
        ],
        tags: ["Presence", "Reputation", "Local SEO"],
    },
    {
        sector: "Boutique Real Estate",
        title: "Repositioned from broker to advisor — and stopped negotiating on fee.",
        outcome: "+62% average commission",
        body: "A premium real-estate firm losing high-value mandates to less credentialed competitors with sharper presence. New narrative, new presence, new trust architecture — the calls that came in were already different.",
        metrics: [
            { value: "+62%", label: "Avg. commission" },
            { value: "2.4×", label: "Qualified leads" },
            { value: "0", label: "Fee negotiations" },
        ],
        tags: ["Positioning", "Trust", "Conversion"],
    },
    {
        sector: "Executive Education Institute",
        title: "Editorial repositioning unlocked tier-one corporate enrolments.",
        outcome: "3.1× cohort revenue · 2 quarters",
        body: "A respected institute with academic credibility but consumer-grade presence. We built a quietly authoritative system — content, design, and reputation — aligned to the buyers that mattered.",
        metrics: [
            { value: "3.1×", label: "Cohort revenue" },
            { value: "+128%", label: "Corporate leads" },
            { value: "2 qtr", label: "Time to result" },
        ],
        tags: ["Authority", "Content", "Growth"],
    },
];

export const STATS = [
    { value: "12+", label: "Industries served" },
    { value: "94%", label: "Client retention" },
    { value: "4–8w", label: "Typical engagement" },
    { value: "1", label: "Standard. Premium." },
];

export const IMAGES = {
    hero: "https://images.unsplash.com/photo-1738844153732-a485f0e78382?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlJTIwbW9ub2Nocm9tZXxlbnwwfHx8fDE3ODA3NTk2NjB8MA&ixlib=rb-4.1.0&q=85",
    about: "https://images.pexels.com/photos/6077439/pexels-photo-6077439.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    process:
        "https://images.unsplash.com/photo-1622396481322-3b83d186701b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlJTIwbW9ub2Nocm9tZXxlbnwwfHx8fDE3ODA3NTk2NjB8MA&ixlib=rb-4.1.0&q=85",
    paper: "https://images.pexels.com/photos/7233376/pexels-photo-7233376.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export const MARQUEE_WORDS = [
    "Presence",
    "Trust",
    "Performance",
    "Growth",
    "Reputation",
    "Visibility",
    "Authority",
    "Clarity",
];

export const mailtoHref = (subject = "Strategy call enquiry") =>
    `mailto:${BRAND.email}?subject=${encodeURIComponent(subject)}`;
